import { AnimationClip, type KeyframeTrack } from 'three'

/**
 * Renames the bone portion of `KeyframeTrack` names using a source→target bone map.
 *
 * Three.js animation track names follow the form `<boneName>.<property>` (e.g.
 * `Hips.quaternion`, `LeftArm.position`, `LeftHand.scale`) or a morph-target form
 * `<boneName>.morphTargetInfluences[<index>]`. This utility splits on the first '.',
 * looks up the bone name in the provided map, and returns a renamed clone.
 *
 * Tracks whose bone is not present in the map are passed through unchanged so a
 * partial map (e.g. one that doesn't include finger bones) still works for the
 * tracks it does cover. The caller can opt to drop unmapped tracks entirely via
 * `dropUnmapped: true` — useful when importing a foreign pack that has tracks for
 * bones the target rig simply doesn't have.
 */
export interface RenameOptions {
  /** When true, tracks whose bone is not in the rename map are removed. Default false. */
  dropUnmapped?: boolean
  /** Optional callback invoked once per unmapped bone name (for diagnostics). */
  onUnmapped?: (bone_name: string) => void
}

export class TrackBoneRenamer {
  /**
   * Returns a new `AnimationClip` whose tracks have their bone names rewritten via the map.
   * The original clip is not mutated.
   */
  static rename_clip (
    clip: AnimationClip,
    bone_name_map: Record<string, string>,
    options: RenameOptions = {}
  ): AnimationClip {
    const seen_unmapped = new Set<string>()
    const renamed_tracks: KeyframeTrack[] = []

    for (const track of clip.tracks) {
      const split_index = track.name.indexOf('.')
      if (split_index === -1) {
        // Track name doesn't have the expected `<bone>.<prop>` shape. Pass through unchanged.
        renamed_tracks.push(track.clone())
        continue
      }

      const bone_name = track.name.slice(0, split_index)
      const property_suffix = track.name.slice(split_index) // includes the leading '.'
      const new_bone_name = bone_name_map[bone_name]

      if (new_bone_name === undefined) {
        if (!seen_unmapped.has(bone_name)) {
          seen_unmapped.add(bone_name)
          options.onUnmapped?.(bone_name)
        }
        if (options.dropUnmapped !== true) {
          renamed_tracks.push(track.clone())
        }
        continue
      }

      const cloned = track.clone()
      cloned.name = `${new_bone_name}${property_suffix}`
      renamed_tracks.push(cloned)
    }

    return new AnimationClip(clip.name, clip.duration, renamed_tracks)
  }

  /** Rename every clip in a list. Returns a new array of new clips; inputs are untouched. */
  static rename_clips (
    clips: AnimationClip[],
    bone_name_map: Record<string, string>,
    options: RenameOptions = {}
  ): AnimationClip[] {
    return clips.map(clip => this.rename_clip(clip, bone_name_map, options))
  }

  /**
   * Returns the set of unique bone names referenced by all tracks in the given clips.
   * Useful for inspecting an unknown imported pack to figure out which mapper to use.
   */
  static collect_track_bone_names (clips: AnimationClip[]): Set<string> {
    const names = new Set<string>()
    for (const clip of clips) {
      for (const track of clip.tracks) {
        const split_index = track.name.indexOf('.')
        if (split_index === -1) continue
        names.add(track.name.slice(0, split_index))
      }
    }
    return names
  }
}
